// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseInputSearch, BaseTable, Loading } from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch } from '~/utils/redux/hooks';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { IHeaderCellType } from '~/utils/interface/common';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  EMPTY_STRING,
  REG_EXP,
} from '~/utils/constants/common';
import { TypeListInventory } from '~/utils/enum/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { IParamInventoryProduct } from '~/utils/interface/inventory';
import { getListInventoryProduct } from '~/thunks/inventory/inventoryThunk';
import { IFormPackage, IPackageProduct, IProductItem } from '~/utils/interface/package';
// Styles, images, icons
import styles from './SelectProductPackageForm.module.scss';
import { icons } from '~/assets';

type Props = {
  detailPackage: IPackageProduct[];
};

const cx = classNames.bind(styles);

const SelectProductPackageForm = (props: Props) => {
  //#region Destructuring Props
  const { detailPackage } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/packages', 'common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IFormPackage>();

  const {
    packageItems,
    productItems,
    selectIdProductSetProduct,
    selectIdVariantSetProduct,
    selectQuantitySetProduct,
    deleteProduct,
  } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IParamInventoryProduct>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
    isHasVariant: true,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (detailPackage.length <= 0) return;

    handleGetListInventory(paramObject, detailPackage);
  }, [detailPackage]);

  useEffect(() => {
    handleGetListInventory(paramObject);
    setValue('selectIdProductInventory', EMPTY_STRING, { shouldDirty: true });
    setValue('selectIdVariantInventory', EMPTY_STRING, { shouldDirty: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: DEFAULT_LIMIT_PAGE,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (!selectIdProductSetProduct) return;

    const productIndex =
      productItems?.findIndex(
        (item) =>
          item.id === selectIdProductSetProduct &&
          String(item.variantId || EMPTY_STRING) === (selectIdVariantSetProduct || EMPTY_STRING)
      ) || 0;

    setValue(`productItems.${productIndex}.quantity`, selectQuantitySetProduct, { shouldDirty: true });
  }, [selectIdProductSetProduct, selectQuantitySetProduct, deleteProduct, selectIdVariantSetProduct]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListInventory = (params: IParamInventoryProduct, detailPackage?: IPackageProduct[]) => {
    setIsLoading(true);

    dispatch(getListInventoryProduct(params))
      .unwrap()
      .then((res) => {
        const { responses: listInventory, pagination } = res?.data;

        if (!listInventory) return;

        const dataProducts: IProductItem[] = listInventory.map((item) => {
          const findQuantity =
            (detailPackage || packageItems)?.find(
              (itemPackage) =>
                itemPackage.inventoryId === item.id &&
                String(itemPackage?.variantId || EMPTY_STRING) === String(item.variantId || EMPTY_STRING)
            )?.quantity || 0;

          return {
            id: item.id,
            name: item.name,
            rentalPrice: item.rentalPrice,
            stock: item.quantity,
            laborCost: EMPTY_STRING,
            quantity: findQuantity,
            isRequired: false,
            isShowPrice: false,
            inventoryImages: item.inventoryImages,
            type: item?.type || EMPTY_STRING,
            variantName: item?.variantName || EMPTY_STRING,
            variantId: item?.variantId || EMPTY_STRING,
          };
        });

        setValue('productItems', dataProducts);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const columnsItem = (t: TFunction): IHeaderCellType<IProductItem>[] => {
    return [
      {
        id: 'name',
        label: t('package_form_product_table_title_name'),
        render: (record: IProductItem, index: number) => (
          <Stack direction={'row'} gap={3} alignItems={'center'}>
            <Avatar
              src={
                record?.inventoryImages && record?.inventoryImages?.length > 0
                  ? record?.inventoryImages[0].url
                  : EMPTY_STRING
              }
              alt={t('common_alt_img', { ns: 'common/base' })}
              variant='rounded'
            />

            <Stack gap={'2px'}>
              <Box>{record.name}</Box>

              {record?.variantName && <Box className={cx('textVariant')}>{record?.variantName}</Box>}
            </Stack>

            {record.type === TypeListInventory.PART && (
              <Box className={cx('addOne')}>{t('table_value_title_add_on')}</Box>
            )}
          </Stack>
        ),
        minWidth: 100,
      },
      {
        id: 'stock',
        label: t('package_form_product_table_title_stock'),
        render: (record: IProductItem, index: number) => record.stock,
        minWidth: 100,
      },
      {
        id: 'rentalPrice',
        label: t('package_form_product_table_title_rental_price'),
        render: (record: IProductItem, index: number) =>
          formatCurrencyUSD({ value: record.rentalPrice, showDecimal: true }),
        minWidth: 100,
      },
      {
        id: 'quantity',
        label: t('package_form_product_table_title_quantity'),
        align: 'center',
        render: (record: IProductItem, index: number) => {
          return (
            <Stack alignItems={'center'}>
              <Controller
                key={`${record.id}-${index}-quantity`}
                name={`productItems.${index}.quantity`}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Stack direction='row' spacing={1} alignItems='center' gap={2} className={cx('viewQuantity')}>
                    <IconButton
                      className={cx('buttonQty')}
                      onClick={() => {
                        const newQty = Math.max(Number(value) - 1, 0);
                        setValue(`productItems.${index}.quantity`, newQty, { shouldDirty: true });
                        setValue(`selectIdProductInventory`, record?.id, { shouldDirty: true });
                        setValue(`selectIdVariantInventory`, record?.variantId, { shouldDirty: true });
                      }}
                    >
                      <img src={icons.commonIconMinus} alt={t('common_alt_img', 'common/base')} />
                    </IconButton>

                    <BaseInput
                      height={31}
                      textAlignCenter
                      width={55}
                      borderRadius={'4px'}
                      value={String(value)}
                      onChange={(e) => {
                        const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                        const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                        onChange(numericValue);
                        setValue(`selectIdProductInventory`, record?.id, { shouldDirty: true });
                        setValue(`selectIdVariantInventory`, record?.variantId, { shouldDirty: true });
                      }}
                      errorMessage={errors.productItems?.[index]?.quantity?.message}
                    />

                    <IconButton
                      className={cx('buttonQty')}
                      onClick={() => {
                        const newQty = Number(value) + 1;
                        setValue(`productItems.${index}.quantity`, newQty, { shouldDirty: true });
                        setValue(`selectIdProductInventory`, record?.id, { shouldDirty: true });
                        setValue(`selectIdVariantInventory`, record?.variantId, { shouldDirty: true });
                      }}
                    >
                      <img src={icons.commonIconPlusGrey} alt={t('common_alt_img', 'common/base')} />
                    </IconButton>
                  </Stack>
                )}
              />
            </Stack>
          );
        },
        minWidth: 120,
      },
    ];
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };
  //#endregion Handle Function

  return (
    <Stack id='addOnInventoryFormComponent' gap={4} className={cx('container')}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography className={cx('header')}>{t('package_form_product_table_title')}</Typography>

        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('package_title_search')}
        />
      </Stack>

      <BaseTable
        dataSource={productItems}
        currentPage={paramObject?.page}
        totalPage={pagination?.totalPages}
        columns={columnsItem(t)}
        onPageChange={handlePageChange}
      />

      {isLoading && <Loading />}
    </Stack>
  );
};

export default SelectProductPackageForm;
